import { useEffect, useState, useMemo } from "react";
import {
  Plus, Copy, Trash2, ChevronDown, ChevronRight, GripVertical,
  Camera, FileText, Search, X, ClipboardList, FileDown,
} from "lucide-react";
import { btn } from "@/components/common/btn";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { SettingsPanel, PanelHeader, PanelToolbar, EmptyState } from "@/components/settings/SettingsUI";
import {
  getInspectionTemplates, createInspectionTemplate, updateInspectionTemplate,
  deleteInspectionTemplate, copyInspectionTemplate, getMasterScopes,
} from "@/lib/api";
import { downloadPdfUrl } from "@/lib/downloadBlob";

const RATING_TYPES = [
  { value: "yes_no", label: "Yes / No" },
  { value: "yes_no_na", label: "Yes / No / N/A" },
  { value: "pass_fail", label: "Pass / Fail" },
  { value: "pass_fail_na", label: "Pass / Fail / N/A" },
  { value: "na_ok", label: "N/A / OK" },
  { value: "photo", label: "Photo Only" },
  { value: "text", label: "Text" },
  { value: "long_text", label: "Long Text" },
];

const newCheckpoint = () => ({
  id: crypto.randomUUID(),
  label: "",
  rating_type: "yes_no",
  allow_photos: true,
  allow_notes: true,
  photo_required_on_fail: false,
  note_required_on_fail: false,
});

const newSection = (name = "") => ({
  id: crypto.randomUUID(),
  name,
  checkpoints: [newCheckpoint()],
});

export default function Inspections() {
  /* ── Template state ── */
  const [templates, setTemplates] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [search, setSearch] = useState("");
  const [editTemplate, setEditTemplate] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [form, setForm] = useState({ name: "", scope_id: "", scope_name: "", description: "", sections: [] });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [tRes, sRes] = await Promise.all([
        getInspectionTemplates(), getMasterScopes(),
      ]);
      setTemplates(tRes.data);
      setScopes(sRes.data);
    } catch { toast.error("Failed to load data"); }
  };

  /* ── Template handlers ── */
  const filtered = useMemo(() => {
    if (!search) return templates;
    const q = search.toLowerCase();
    return templates.filter((t) =>
      t.name.toLowerCase().includes(q) || (t.scope_name || "").toLowerCase().includes(q) || (t.description || "").toLowerCase().includes(q)
    );
  }, [templates, search]);

  const openNew = () => {
    setEditTemplate(null);
    const s = newSection("General");
    setForm({ name: "", scope_id: "", scope_name: "", description: "", sections: [s] });
    setExpandedSections({ [s.id]: true });
    setShowDialog(true);
  };

  const openEdit = (t) => {
    setEditTemplate(t);
    setForm({
      name: t.name, scope_id: t.scope_id || "", scope_name: t.scope_name || "",
      description: t.description || "",
      sections: t.sections?.length ? t.sections : [newSection("General")],
    });
    const expanded = {};
    (t.sections || []).forEach((s) => { expanded[s.id] = true; });
    setExpandedSections(expanded);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Template name is required"); return; }
    try {
      if (editTemplate) {
        await updateInspectionTemplate(editTemplate.id, form);
        toast.success("Template updated");
      } else {
        await createInspectionTemplate(form);
        toast.success("Template created");
      }
      setShowDialog(false);
      loadData();
    } catch { toast.error("Failed to save template"); }
  };

  const handleDelete = async (id) => { try { await deleteInspectionTemplate(id); toast.success("Template deleted"); loadData(); } catch { toast.error("Failed to delete"); } };
  const handleCopy = async (id) => { try { await copyInspectionTemplate(id); toast.success("Template copied"); loadData(); } catch { toast.error("Failed to copy"); } };

  const addSection = () => { const s = newSection(""); setForm((f) => ({ ...f, sections: [...f.sections, s] })); setExpandedSections((p) => ({ ...p, [s.id]: true })); };
  const updateSection = (sid, key, val) => { setForm((f) => ({ ...f, sections: f.sections.map((s) => (s.id === sid ? { ...s, [key]: val } : s)) })); };
  const removeSection = (sid) => { setForm((f) => ({ ...f, sections: f.sections.filter((s) => s.id !== sid) })); };
  const toggleSection = (sid) => { setExpandedSections((p) => ({ ...p, [sid]: !p[sid] })); };
  const addCheckpoint = (sid) => { setForm((f) => ({ ...f, sections: f.sections.map((s) => (s.id === sid ? { ...s, checkpoints: [...s.checkpoints, newCheckpoint()] } : s)) })); };
  const updateCheckpoint = (sid, cpId, key, val) => { setForm((f) => ({ ...f, sections: f.sections.map((s) => (s.id === sid ? { ...s, checkpoints: s.checkpoints.map((cp) => (cp.id === cpId ? { ...cp, [key]: val } : cp)) } : s)) })); };
  const removeCheckpoint = (sid, cpId) => { setForm((f) => ({ ...f, sections: f.sections.map((s) => (s.id === sid ? { ...s, checkpoints: s.checkpoints.filter((cp) => cp.id !== cpId) } : s)) })); };
  const totalCheckpoints = (t) => (t.sections || []).reduce((sum, s) => sum + (s.checkpoints?.length || 0), 0);

  const sortedScopes = useMemo(() => [...scopes].sort((a, b) => (a.name || "").localeCompare(b.name || "")), [scopes]);

  return (
    <div className="space-y-3">
      <SettingsPanel>
        <PanelHeader
          icon={ClipboardList}
          title="Inspection Templates"
          count={filtered.length || null}
          hint="Checklists technicians run in the field"
          testId="inspections-title"
        />
        <PanelToolbar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search templates…"
          searchTestId="search-templates"
          actions={
            <button type="button" className={btn("write")} onClick={openNew} data-testid="btn-new-template"><Plus className="w-3.5 h-3.5" />Template</button>
          }
        />

      <div className="px-5 py-4 space-y-3">
        {filtered.map((t) => (
          <div key={t.id} className="border border-slate-200 rounded-lg p-4 bg-white hover:bg-slate-50/60 transition-colors" data-testid={`template-${t.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1 cursor-pointer" onClick={() => openEdit(t)}>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-slate-900">{t.name}</h3>
                  {t.scope_name && <span className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-700">{t.scope_name}</span>}
                </div>
                {t.description && <p className="text-sm text-slate-500 mt-1">{t.description}</p>}
                <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                  <span>{(t.sections || []).length} section{(t.sections || []).length !== 1 ? "s" : ""}</span>
                  <span>{totalCheckpoints(t)} checkpoint{totalCheckpoints(t) !== 1 ? "s" : ""}</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => downloadPdfUrl(`/inspection-templates/${t.id}/fillable-pdf`, { suggestedName: `${(t.name || "inspection").replace(/\s+/g, "-")}-fillable.pdf`, label: "fillable form" })} title="Download a blank fillable PDF of this checklist (paper fallback)" data-testid={`fillable-template-${t.id}`}><FileDown className="w-4 h-4 text-slate-400" /></Button>
                <Button variant="ghost" size="sm" onClick={() => handleCopy(t.id)} title="Copy template" data-testid={`copy-template-${t.id}`}><Copy className="w-4 h-4 text-slate-400" /></Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)} title="Delete template" data-testid={`delete-template-${t.id}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <EmptyState icon={FileText} title="No templates yet" hint="Create one to get started." />
        )}
      </div>
      </SettingsPanel>

      {/* ═══ Template Editor Dialog ═══ */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editTemplate ? "Edit Template" : "New Inspection Template"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Template Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Trash Chute Inspection" data-testid="input-template-name" />
              </div>
              <div>
                <Label className="text-xs">Scope</Label>
                <Select value={form.scope_id || "none"} onValueChange={(v) => { const scope = sortedScopes.find((s) => s.id === v); setForm({ ...form, scope_id: v === "none" ? "" : v, scope_name: scope?.name || "" }); }}>
                  <SelectTrigger data-testid="select-template-scope"><SelectValue placeholder="Select scope (optional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (General)</SelectItem>
                    {sortedScopes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs">Description</Label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[50px] resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" placeholder="Brief description of this inspection template" data-testid="input-template-description" />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Sections & Checkpoints</Label>
                <Button type="button" variant="outline" size="sm" onClick={addSection} data-testid="btn-add-section"><Plus className="w-3 h-3 mr-1" />Section</Button>
              </div>

              {form.sections.map((section, sIdx) => (
                <div key={section.id} className="border rounded-lg bg-slate-50/50">
                  <div className="flex items-center gap-2 p-3 cursor-pointer" onClick={() => toggleSection(section.id)}>
                    {expandedSections[section.id] ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    <Input value={section.name} onChange={(e) => { e.stopPropagation(); updateSection(section.id, "name", e.target.value); }} onClick={(e) => e.stopPropagation()} placeholder={`Section ${sIdx + 1} name`} className="h-8 text-sm font-medium flex-1 bg-white" data-testid={`section-name-${sIdx}`} />
                    <span className="text-xs text-slate-400 whitespace-nowrap">{section.checkpoints.length} item{section.checkpoints.length !== 1 ? "s" : ""}</span>
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeSection(section.id); }} className="h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                  </div>
                  {expandedSections[section.id] && (
                    <div className="px-3 pb-3 space-y-2">
                      {section.checkpoints.map((cp, cpIdx) => (
                        <div key={cp.id} className="flex items-start gap-2 bg-white rounded-md p-2 border border-slate-100">
                          <GripVertical className="w-4 h-4 text-slate-300 mt-1.5 flex-shrink-0" />
                          <div className="flex-1 space-y-1.5">
                            <Input value={cp.label} onChange={(e) => updateCheckpoint(section.id, cp.id, "label", e.target.value)} placeholder="Checkpoint question/item" className="h-8 text-sm" data-testid={`checkpoint-label-${sIdx}-${cpIdx}`} />
                            <div className="flex items-center gap-3 flex-wrap">
                              <Select value={cp.rating_type} onValueChange={(v) => updateCheckpoint(section.id, cp.id, "rating_type", v)}>
                                <SelectTrigger className="h-7 w-36 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>{RATING_TYPES.map((rt) => <SelectItem key={rt.value} value={rt.value} className="text-xs">{rt.label}</SelectItem>)}</SelectContent>
                              </Select>
                              <label className="flex items-center gap-1 text-xs text-slate-500 cursor-pointer">
                                <input type="checkbox" checked={cp.allow_photos} onChange={(e) => updateCheckpoint(section.id, cp.id, "allow_photos", e.target.checked)} className="rounded border-slate-300" />
                                <Camera className="w-3 h-3" />Photos
                              </label>
                              <label className="flex items-center gap-1 text-xs text-slate-500 cursor-pointer">
                                <input type="checkbox" checked={cp.allow_notes} onChange={(e) => updateCheckpoint(section.id, cp.id, "allow_notes", e.target.checked)} className="rounded border-slate-300" />
                                <FileText className="w-3 h-3" />Notes
                              </label>
                              <label className="flex items-center gap-1 text-xs text-red-400 cursor-pointer" title="Require photo when answer is No/Fail">
                                <input type="checkbox" checked={cp.photo_required_on_fail || false} onChange={(e) => updateCheckpoint(section.id, cp.id, "photo_required_on_fail", e.target.checked)} className="rounded border-red-300" />
                                Req Photo on Fail
                              </label>
                              <label className="flex items-center gap-1 text-xs text-red-400 cursor-pointer" title="Require note when answer is No/Fail">
                                <input type="checkbox" checked={cp.note_required_on_fail || false} onChange={(e) => updateCheckpoint(section.id, cp.id, "note_required_on_fail", e.target.checked)} className="rounded border-red-300" />
                                Req Note on Fail
                              </label>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => removeCheckpoint(section.id, cp.id)} className="h-7 w-7 p-0 mt-0.5"><X className="w-3.5 h-3.5 text-red-400" /></Button>
                        </div>
                      ))}
                      <Button type="button" variant="outline" size="sm" onClick={() => addCheckpoint(section.id)} className="w-full text-xs h-7" data-testid={`btn-add-checkpoint-${sIdx}`}><Plus className="w-3 h-3 mr-1" />Add Checkpoint</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="btn-save-template">{editTemplate ? "Update Template" : "Create Template"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
